import { planRoute } from './astar';
import { inflateGzip, readTile } from './format';
import { RoutingGraph } from './graph';
import {
  RoutingUnavailable, deviceStore, fetchFile, fileUrl, readManifest, readPointer, tilesFor,
} from './tiles';

/*
 * One route request, start to finish: the current version, its trunk and the
 * tiles near the start and the target (widened once when no way is found),
 * merged into the graph this version already holds, then the search.
 *
 * Answers {status: 'ok', route, version, attribution} | {status: 'no_route'}
 * | {status: 'unavailable'} (no data reachable, online or kept) -- never
 * throws for those, so the map can fall back to the straight line.
 */
export const RADII_KM = [15, 40];
const graphs = new Map(); // version -> RoutingGraph, the last one only

export function forgetGraphs() {
  graphs.clear();
}

/** The routing files of an area ([south, west, north, east]) -- the trunk and
 *  the tiles it touches -- and their total size, from the manifest. */
export async function routingFilesFor({ pointerUrl, bbox }, deps = {}) {
  const fetchFn = deps.fetchFn || fetch;
  const { version, manifestUrl } = await readPointer(pointerUrl, { fetchFn, storage: deps.storage });
  const store = deps.store || deviceStore(version);
  const manifest = await readManifest(manifestUrl, { store, fetchFn, hash: deps.hash });
  const [south, west, north, east] = bbox;
  const corners = [{ lat: south, lng: west }, { lat: north, lng: east }, { lat: south, lng: east },
    { lat: north, lng: west }, { lat: (south + north) / 2, lng: (west + east) / 2 }];
  const entries = [manifest.trunk, ...tilesFor(manifest, corners, 0.1)];
  return { version, manifestUrl, store, entries, bytes: entries.reduce((n, e) => n + (e.bytes || 0), 0) };
}

/** Fetch and keep an area's routing files, so a route there is planned offline. */
export async function saveRoutingArea({ pointerUrl, bbox }, deps = {}) {
  const fetchFn = deps.fetchFn || fetch;
  const { manifestUrl, store, entries, bytes } = await routingFilesFor({ pointerUrl, bbox }, deps);
  for (const entry of entries) {
    await fetchFile(fileUrl(manifestUrl, entry.file), { store, fetchFn, hash: deps.hash, sha256: entry.sha256 });
  }
  return { files: entries.length, bytes };
}

export async function route({ pointerUrl, start, target, mode }, deps = {}) {
  const fetchFn = deps.fetchFn || fetch;
  const inflate = deps.inflate || inflateGzip;
  try {
    const { version, manifestUrl } = await readPointer(pointerUrl, { fetchFn, storage: deps.storage });
    const store = deps.store || deviceStore(version);
    const fileDeps = { store, fetchFn, hash: deps.hash };
    const manifest = await readManifest(manifestUrl, fileDeps);
    let graph = graphs.get(version);
    if (!graph) {
      graphs.clear();
      graph = new RoutingGraph();
      graphs.set(version, graph);
    }
    for (const radiusKm of RADII_KM) {
      const wanted = [manifest.trunk, ...tilesFor(manifest, [start, target], radiusKm)]
        .filter((entry) => !graph.tiles.has(entry.file));
      const loaded = await Promise.all(wanted.map(async (entry) => ({
        entry,
        tile: await readTile(await fetchFile(fileUrl(manifestUrl, entry.file), { ...fileDeps, sha256: entry.sha256 }),
          inflate),
      })));
      for (const { entry, tile } of loaded) graph.addTile(tile, entry.file);
      const found = planRoute(graph, start, target, mode);
      if (found) return { status: 'ok', route: found, version, attribution: manifest.attribution };
    }
    return { status: 'no_route', version, attribution: manifest.attribution };
  } catch (e) {
    if (e instanceof RoutingUnavailable) return { status: 'unavailable' };
    return { status: 'error', error: String(e?.message || e) };
  }
}
